/**
 * Helpers for the `active_investing_entity` deep-link parameter, which arrives
 * on emailed links (document notices, impersonation links) and asks the portal
 * to switch to a specific investing entity before showing the page.
 *
 * An authenticated visitor can act on it immediately. An unauthenticated one
 * cannot: the switch is a mutation behind `WithAuthentication`, so the intent
 * has to survive a login and possibly an MFA step. Carrying it through the URL
 * is unreliable — server guards such as `documents/layout.tsx` redirect without
 * the query string, and `useRedirectToLogin` prefers an existing `returnUrl`
 * over the current location — so the intent is parked in session storage
 * instead and replayed once a session exists.
 */

const ACTIVE_INVESTING_ENTITY_PARAM = "active_investing_entity";
const RETURN_URL_PARAM = "returnUrl";
const LONG_LIVED_TOKEN_PARAM = "llt";

const STORAGE_KEY = "pending_active_investing_entity";

/**
 * A parked intent only has to outlive a login plus an MFA step. Bounding it
 * stops an abandoned login from firing a switch on an unrelated page load
 * later in the same tab.
 */
const EXPIRY_MS = 10 * 60 * 1000;

type PendingActiveInvestingEntity = {
	id: string;
	createdAt: number;
};

/**
 * `returnUrl` is a path, not an absolute URL, so it needs a base to parse
 * against. The base is discarded — only the search params are read.
 */
function parseReturnUrl(returnUrl: string): URL | null {
	try {
		return new URL(returnUrl, "http://n");
	} catch {
		return null;
	}
}

/**
 * Reads the requested investing entity, checking the top-level params first and
 * then inside `returnUrl`. The nested read covers links minted before the
 * parking behaviour existed, and redirects that were already in flight.
 */
export function extractActiveInvestingEntity(
	searchParams: URLSearchParams,
): string | null {
	const direct = searchParams.get(ACTIVE_INVESTING_ENTITY_PARAM);
	if (direct) {
		return direct;
	}

	const returnUrl = searchParams.get(RETURN_URL_PARAM);
	if (!returnUrl) {
		return null;
	}

	return (
		parseReturnUrl(returnUrl)?.searchParams.get(
			ACTIVE_INVESTING_ENTITY_PARAM,
		) ?? null
	);
}

/**
 * Rebuilds the current location without the deep-link parameters, removing them
 * from the top-level query and from inside `returnUrl`.
 *
 * `llt` goes too. By the time this runs the token has already been exchanged
 * for a session by InitializeApp, and it is single-use — leaving it in the URL
 * means a later reload tries to exchange a dead token and logs the impersonated
 * user out.
 */
export function stripActiveInvestingEntity(
	pathname: string,
	searchParams: URLSearchParams,
): string {
	const params = new URLSearchParams(searchParams.toString());
	params.delete(ACTIVE_INVESTING_ENTITY_PARAM);
	params.delete(LONG_LIVED_TOKEN_PARAM);

	const returnUrl = params.get(RETURN_URL_PARAM);
	if (returnUrl) {
		const parsed = parseReturnUrl(returnUrl);
		if (parsed) {
			parsed.searchParams.delete(ACTIVE_INVESTING_ENTITY_PARAM);
			parsed.searchParams.delete(LONG_LIVED_TOKEN_PARAM);
			params.set(RETURN_URL_PARAM, `${parsed.pathname}${parsed.search}`);
		}
	}

	const query = params.toString();
	return `${pathname}${query ? `?${query}` : ""}`;
}

/**
 * Resolved on each call rather than at module scope: this module is imported by
 * a component that also renders on the server, and the unit tests install a
 * stub after import.
 */
function getSessionStorage(): Storage | null {
	try {
		return globalThis.sessionStorage ?? null;
	} catch {
		// Access itself throws when storage is disabled.
		return null;
	}
}

export function writePendingActiveInvestingEntity(id: string) {
	try {
		const pending: PendingActiveInvestingEntity = { id, createdAt: Date.now() };
		getSessionStorage()?.setItem(STORAGE_KEY, JSON.stringify(pending));
	} catch {
		// Storage is unavailable (Safari private browsing, quota, disabled). The
		// deep link degrades to landing on the page without switching, which is
		// better than breaking the navigation.
	}
}

export function clearPendingActiveInvestingEntity() {
	try {
		getSessionStorage()?.removeItem(STORAGE_KEY);
	} catch {
		// See writePendingActiveInvestingEntity.
	}
}

/**
 * In-memory replay guard for `RouteActiveInvestingEntity`. The component lives
 * in the root layout, so a ref survives client navigations for the life of the
 * tab.
 *
 * After a completed switch there is no requested or pending id — clear the key
 * so the same emailed link can fire again later (e.g. the investor changed
 * entity, then opened the same notice). While an id is still present, keep the
 * key so a failed sessionStorage clear cannot loop.
 */
export function nextHandledSwitch(
	handledKey: string | null,
	investingEntityId: string | null,
	returnUrl: string,
): { handledKey: string | null; shouldNavigate: boolean } {
	if (!investingEntityId) {
		return { handledKey: null, shouldNavigate: false };
	}

	const nextKey = `${investingEntityId}:${returnUrl}`;
	if (handledKey === nextKey) {
		return { handledKey, shouldNavigate: false };
	}

	return { handledKey: nextKey, shouldNavigate: true };
}

export function readPendingActiveInvestingEntity(): string | null {
	try {
		const raw = getSessionStorage()?.getItem(STORAGE_KEY);
		if (!raw) {
			return null;
		}

		const pending = JSON.parse(raw) as Partial<PendingActiveInvestingEntity>;
		if (!pending?.id || typeof pending.createdAt !== "number") {
			clearPendingActiveInvestingEntity();
			return null;
		}

		if (Date.now() - pending.createdAt > EXPIRY_MS) {
			clearPendingActiveInvestingEntity();
			return null;
		}

		return pending.id;
	} catch {
		return null;
	}
}
